import { Entity, GraphNode } from "playcanvas";

export function isEntity(node: unknown): node is Entity {
	return node instanceof Entity;
}

export function flatMapNodeHierarchy<T extends GraphNode = GraphNode>(
	roots: GraphNode | GraphNode[],
	filter?: ((node: GraphNode) => node is T) | ((node: GraphNode) => boolean),
): T[] {
	const result: T[] = [];
	function visitNode(node: GraphNode) {
		if (!filter || filter(node)) {
			result.push(node as T);
		}
	}
	const rootNodes = Array.isArray(roots) ? roots : [roots];

	for (const rootNode of rootNodes) {
		// eslint-disable-next-line unicorn/no-array-for-each
		rootNode.forEach(visitNode);
	}

	return result;
}

/**
 * Walks up the parent chain of both graph nodes until a common ancestor is found.
 * @param childA First child node
 * @param childB Second child node
 * @param stopBeforeAncestor When specified, undefined will be returned if this node is encountered.
 * This is useful in cases where you are only interested in matches inside of a specific part of the hierarchy.
 * @returns The found common ancestor (if any)
 */
export function findCommonAncestor(
	childA: GraphNode,
	childB: GraphNode,
	stopBeforeAncestor?: GraphNode,
): GraphNode | undefined {
	let currentA: GraphNode | null = childA;
	let currentB: GraphNode | null = childB;
	let depthA = currentA.graphDepth;
	let depthB = currentB.graphDepth;

	while (
		currentA &&
		currentB &&
		currentA !== currentB &&
		currentA !== stopBeforeAncestor &&
		currentB !== stopBeforeAncestor
	) {
		const stepA = depthA >= depthB;
		const stepB = depthB >= depthA;

		if (stepA) {
			depthA--;
			currentA = currentA.parent;
		}
		if (stepB) {
			depthB--;
			currentB = currentB.parent;
		}
	}

	return currentA === currentB &&
		currentA !== stopBeforeAncestor &&
		currentB !== stopBeforeAncestor
		? currentA ?? undefined
		: undefined;
}

export function findParent<T extends GraphNode>(
	child: T,
	predicate: (node: NoInfer<T>) => boolean,
): T | undefined {
	let parent = child.parent;
	while (parent) {
		if (predicate(parent as T)) {
			return parent as T;
		}
		parent = parent.parent;
	}
	return undefined;
}
